// client/src/components/pages/TelegramBI/ScheduleManager/SchedulePreviewModal.js
import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaEye,
  FaPlay,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTelegramPlane,
  FaRedo,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const SchedulePreviewModal = ({ schedule, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [templateResult, setTemplateResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (schedule) {
      generatePreview();
    }
  }, [schedule]);

  const generatePreview = async () => {
    setLoading(true);
    setError(null);
    setQueryResult(null);
    setTemplateResult(null);

    try {
      if (!schedule.query_id) {
        throw new Error(
          'Schedule is missing a QueryID. Ensure the schedule has a valid query assigned.'
        );
      }
      if (!schedule.template_id) {
        throw new Error(
          'Schedule is missing a TemplateID. Ensure the schedule has a valid template assigned.'
        );
      }

      // Execute the query
      const queryResponse = await api.telegramBI.queries.execute(schedule.query_id, {});
      const qData = queryResponse.data?.data || queryResponse.data;
      setQueryResult(qData);

      // Extract data rows for template
      const queryRows = qData?.data || [];

      // Process template with query results
      const templateResponse = await api.telegramBI.templates.preview(schedule.template_id, {
        queryResults: queryRows,
        variables: {
          scheduleName: schedule.schedule_name,
          executedAt: new Date().toISOString(),
        },
      });
      const tData = templateResponse.data?.data || templateResponse.data;
      setTemplateResult(tData);
    } catch (err) {
      console.error('Preview generation failed:', err);
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Preview generation failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    setSending(true);
    try {
      await api.telegramBI.schedules.execute(schedule.schedule_id);
      toast.success('Test message sent successfully');
    } catch (err) {
      console.error('Send test failed:', err);
      toast.error(err.response?.data?.message || 'Failed to send test message');
    } finally {
      setSending(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin h-6 w-6 text-primary-500 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Generating preview...</p>
            <p className="text-xs text-gray-400 mt-1">Executing query and rendering template</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <FaExclamationTriangle className="h-8 w-8 text-red-400 mx-auto mb-3" />
            <p className="text-sm text-red-600 mb-3">{error}</p>
            <button
              onClick={generatePreview}
              className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <FaRedo size={11} />
              <span>Retry</span>
            </button>
          </div>
        </div>
      );
    }

    if (!templateResult) {
      return (
        <div className="flex items-center justify-center h-64">
          <p className="text-sm text-gray-400">No preview available</p>
        </div>
      );
    }

    const messageContent = templateResult.content || templateResult.preview || '';

    return (
      <div className="space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Query Rows</p>
            <p className="text-lg font-semibold text-gray-800">{queryResult?.rowCount ?? 0}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Message Length</p>
            <p className="text-lg font-semibold text-gray-800">{messageContent.length}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Status</p>
            <p
              className={`text-lg font-semibold ${templateResult.truncated ? 'text-amber-600' : 'text-green-600'}`}
            >
              {templateResult.truncated ? 'Truncated' : 'Complete'}
            </p>
          </div>
        </div>

        {/* Target Chat Info */}
        <div className="flex items-center space-x-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <FaTelegramPlane size={12} className="text-blue-500" />
          <span>
            Target:{' '}
            <span className="font-medium text-gray-700">
              {schedule.target_chat_name || `Chat ${schedule.telegram_chat_id || 'N/A'}`}
            </span>
          </span>
        </div>

        {/* Telegram Chat Bubble Preview */}
        <div className="rounded-lg overflow-hidden border border-gray-200">
          {/* Chat Header */}
          <div className="bg-primary-600 px-4 py-2.5 flex items-center space-x-3">
            <div className="w-8 h-8 bg-surface bg-opacity-20 rounded-full flex items-center justify-center">
              <FaTelegramPlane className="text-white" size={14} />
            </div>
            <div>
              <p className="text-white text-sm font-medium">LexiBridge Bot</p>
              <p className="text-primary-200 text-xs">bot</p>
            </div>
          </div>

          {/* Chat Body */}
          <div className="bg-gray-100 p-4 min-h-[180px] max-h-[360px] overflow-y-auto">
            {/* Message bubble */}
            <div className="max-w-[85%]">
              <div className="bg-surface rounded-lg rounded-tl-none p-3 shadow-sm">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                  {messageContent}
                </pre>
                <div className="flex items-center justify-end mt-1.5 space-x-1">
                  <span className="text-[10px] text-gray-400">
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <FaCheckCircle size={10} className="text-blue-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Truncation Warning */}
        {templateResult.truncated && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
            <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" size={13} />
            <div>
              <p className="text-xs font-medium text-amber-800">Message truncated</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Original length: {templateResult.originalLength || messageContent.length}{' '}
                characters. Telegram allows a maximum of 4096 characters per message.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <FaEye className="text-primary-500" size={16} />
              <span>Schedule Preview</span>
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{schedule?.schedule_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{renderContent()}</div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={generatePreview}
            disabled={loading}
            className="flex items-center space-x-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-surface border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <FaRedo size={11} />
            <span>Refresh Preview</span>
          </button>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleSendTest}
              disabled={sending || loading || !!error}
              className="flex items-center space-x-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <>
                  <FaSpinner size={11} className="animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <FaPlay size={11} />
                  <span>Send Test</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulePreviewModal;
